import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import videoService from '../services/videoService';
import categoryService from '../services/categoryService';

const VideoUpload = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    url: '',
    title: '',
    description: '',
    videoType: 'video',
    category: ''
  });
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [autoGenerateThumbnail, setAutoGenerateThumbnail] = useState(false);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatingThumbnail, setGeneratingThumbnail] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      console.log('🔄 Fetching categories from API...');
      const data = await categoryService.getAllCategories();
      console.log('📦 Categories API Response:', data);
      console.log('📋 Categories Array:', data.categories);
      console.log('🔢 Number of categories:', data.categories?.length || 0);
      
      if (data && data.categories) {
        if (data.categories.length > 0) {
          setCategories(data.categories);
          console.log('✅ Categories loaded successfully:', data.categories.length, 'categories');
          data.categories.forEach(cat => console.log(`   - ${cat.name} (ID: ${cat._id})`));
        } else {
          console.warn('⚠️ No categories found in database. Please seed categories first.');
          setCategories([]);
        }
      } else {
        console.error('❌ Invalid API response format:', data);
        setCategories([]);
      }
    } catch (err) {
      console.error('❌ Failed to fetch categories:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        url: err.config?.url
      });
      setCategories([]);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      setThumbnailFile(file);
      setAutoGenerateThumbnail(false); // Disable auto-generate when file is uploaded
      setError('');

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGenerateThumbnail = async () => {
    if (!formData.url) {
      setError('Please enter a YouTube URL first');
      return;
    }

    setGeneratingThumbnail(true);
    setError('');
    setSuccess('');

    try {
      console.log('🔄 Generating thumbnail for URL:', formData.url);
      const response = await videoService.generateThumbnail(formData.url);
      console.log('✅ Thumbnail response:', response);
      
      if (response.thumbnails) {
        // Try maxres first, fallback to hq if not available
        const thumbnailUrl = response.thumbnails.maxres || response.thumbnails.hq || response.thumbnails.mq;
        
        if (thumbnailUrl) {
          setThumbnailPreview(thumbnailUrl);
          setAutoGenerateThumbnail(true);
          setThumbnailFile(null); // Clear any uploaded file
          setSuccess('Thumbnail generated successfully!');
          
          // Clear success message after 3 seconds
          setTimeout(() => setSuccess(''), 3000);
        } else {
          console.error('❌ No thumbnail URLs in response:', response);
          setError('Could not generate thumbnail from this URL. Please check if it\'s a valid YouTube URL.');
        }
      } else {
        console.error('❌ Invalid response format:', response);
        setError('Could not generate thumbnail from this URL. Please check if it\'s a valid YouTube URL.');
      }
    } catch (err) {
      console.error('❌ Thumbnail generation error:', err);
      console.error('Error details:', {
        message: err.message,
        error: err.error,
        response: err.response,
        data: err.response?.data
      });
      
      // Extract error message from various possible formats
      let errorMessage = 'Failed to generate thumbnail. ';
      
      if (err.response?.data?.message) {
        errorMessage += err.response.data.message;
      } else if (err.message) {
        errorMessage += err.message;
      } else if (err.error) {
        errorMessage += err.error;
      } else {
        errorMessage += 'Please check if the URL is a valid YouTube video URL.';
      }
      
      setError(errorMessage);
    } finally {
      setGeneratingThumbnail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate thumbnail is provided
    if (!thumbnailFile && !autoGenerateThumbnail) {
      setError('Thumbnail is required. Please upload an image or generate one from YouTube.');
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        thumbnail: thumbnailFile,
        autoGenerateThumbnail: autoGenerateThumbnail
      };

      await videoService.createVideo(submitData);
      setSuccess('Video uploaded successfully!');
      setFormData({
        url: '',
        title: '',
        description: '',
        videoType: 'video',
        category: ''
      });
      setThumbnailFile(null);
      setThumbnailPreview('');
      setAutoGenerateThumbnail(false);
      
      setTimeout(() => {
        navigate('/videos');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to upload video. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Sidebar />
      <div className="dashboard">
        <Navbar />
        <div className="container">
        
        <div className="auth-card" style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h1>Upload Video</h1>
          <p>Add a new YouTube video to your collection</p>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="videoType">Type *</label>
              <select
                id="videoType"
                name="videoType"
                value={formData.videoType}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="video">Video</option>
                <option value="shorts">Shorts</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">-- Select Category (Optional) --</option>
                {categories && categories.length > 0 ? (
                  categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>Loading categories...</option>
                )}
              </select>
              {categories && categories.length === 0 && (
                <p style={{ fontSize: '13px', color: '#ef4444', marginTop: '8px' }}>
                  ⚠️ No categories found. Please seed categories first by running: <code style={{ backgroundColor: '#1a1a1a', padding: '2px 6px', borderRadius: '4px' }}>node backend/scripts/seedCategories.js</code>
                </p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="url">YouTube Video URL *</label>
              <input
                type="url"
                id="url"
                name="url"
                value={formData.url}
                onChange={handleChange}
                required
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="Enter video title"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                placeholder="Enter video description"
              />
            </div>

            <div className="form-group">
              <label htmlFor="thumbnail">Thumbnail Image *</label>
              
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleGenerateThumbnail}
                  disabled={generatingThumbnail || !formData.url}
                  style={{
                    flex: '1',
                    padding: '12px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    backgroundColor: generatingThumbnail ? '#999' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: generatingThumbnail || !formData.url ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {generatingThumbnail ? '🔄 Generating...' : '🎬 Generate from YouTube'}
                </button>
                
                <label 
                  htmlFor="thumbnail" 
                  style={{
                    flex: '1',
                    padding: '12px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  📁 Upload Image
                </label>
              </div>

              <input
                type="file"
                id="thumbnail"
                name="thumbnail"
                onChange={handleFileChange}
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                style={{ display: 'none' }}
              />
              
              <small style={{ color: '#666', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                Generate thumbnail from YouTube URL or upload your own image (JPEG, PNG, GIF, WebP - Max 5MB)
              </small>
              
              {thumbnailPreview && (
                <div style={{ marginTop: '15px', position: 'relative' }}>
                  <img 
                    src={thumbnailPreview} 
                    alt="Thumbnail preview" 
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      borderRadius: '8px',
                      border: '2px solid #667eea',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      display: 'block'
                    }}
                    onError={(e) => {
                      console.log('❌ Thumbnail failed to load, trying fallback...');
                      // If maxres fails, try hq quality
                      if (e.target.src.includes('maxresdefault')) {
                        e.target.src = e.target.src.replace('maxresdefault.jpg', 'hqdefault.jpg');
                      } 
                      // If hq fails, try mq quality
                      else if (e.target.src.includes('hqdefault')) {
                        e.target.src = e.target.src.replace('hqdefault.jpg', 'mqdefault.jpg');
                      }
                      // If all fail, show error message
                      else {
                        e.target.style.display = 'none';
                        const errorDiv = e.target.nextElementSibling;
                        if (errorDiv && errorDiv.classList.contains('thumbnail-error')) {
                          errorDiv.style.display = 'block';
                        }
                      }
                    }}
                  />
                  <div 
                    className="thumbnail-error"
                    style={{
                      display: 'none',
                      padding: '20px',
                      backgroundColor: '#fee',
                      border: '2px solid #f00',
                      borderRadius: '8px',
                      color: '#c00',
                      textAlign: 'center'
                    }}
                  >
                    ⚠️ Could not load thumbnail. Please try uploading an image instead.
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setThumbnailPreview('');
                      setThumbnailFile(null);
                      setAutoGenerateThumbnail(false);
                    }}
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '30px',
                      height: '30px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  >
                    ×
                  </button>
                  {autoGenerateThumbnail && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      backgroundColor: '#dbeafe',
                      color: '#1e40af',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}>
                      ✓ Auto-generated from YouTube
                    </div>
                  )}
                </div>
              )}
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Uploading...' : 'Submit'}
            </button>

            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => navigate('/dashboard')}
              disabled={loading}
            >
              Cancel
            </button>
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default VideoUpload;
